import { MutationResult } from "@apollo/client/react";
import {
  AdminStudyProgrammeFragment,
  ContentStatus,
  ExamEditPageData_examSyllabusById,
} from "__generated__/types";
import { CancelOrSubmit } from "common/buttons";
import { FieldError } from "common/components/forms/FieldError";
import { Label } from "common/components/forms/Label";
import { MarkdownField } from "common/components/forms/MarkdownField";
import { MutationLoadingOrError } from "common/components/forms/MutationLoadingOrError";
import { TextField } from "common/components/forms/TextField";
import { InputField } from "common/components/InputField";
import { Select, SelectOption } from "common/components/Select";
import { Spinner } from "common/components/Spinner";
import { contentStatusOptions } from "common/content-status";
import { extractNullableMarkdownContent } from "common/input-types";
import { contentToSelectOption } from "domain/admin/util";
import { REQUIRED_FORM_FIELD } from "domain/constants";
import * as React from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import ReactSelect from "react-select";

const DatePicker = React.lazy(() =>
  import(
    /* webpackChunkName: "DatePicker" */ "common/components/dates/DatePicker"
  ).then((it) => ({
    default: it.DatePicker,
  })),
);

interface Props {
  onSubmit: (input: ExamSyllabusFormValues) => void;
  onCancel: () => void;
  canPublish: boolean;
  submitText?: string;
  parent?: AdminStudyProgrammeFragment | undefined;
  options?: SelectOption[] | null;
  examSyllabus?: ExamEditPageData_examSyllabusById;
  loading: MutationResult["loading"];
  error: MutationResult["error"];
}

const validateStudyPoints = (input: number): string | undefined => {
  const isValid = input > 0 && input <= 100;
  return isValid ? undefined : "Oppgi en verdi mellom 0 og 100.";
};

export interface ExamSyllabusFormValues {
  title: string;
  status: ContentStatus;
  description: string;
  studyPoints: number;
  examDate: string;
  studyProgramme: { value: string; label: string };
}

export const ExamSyllabusForm: React.FC<React.PropsWithChildren<Props>> = ({
  onSubmit,
  onCancel,
  submitText,
  examSyllabus,
  canPublish,
  parent,
  loading,
  error,
}) => {
  const methods = useForm<ExamSyllabusFormValues>({
    defaultValues: {
      studyPoints: examSyllabus?.studyPoints,
      title: examSyllabus?.title,
      description: examSyllabus?.description
        ? (extractNullableMarkdownContent(examSyllabus.description) ?? "")
        : "",
      studyProgramme: parent ? contentToSelectOption(parent) : undefined,
      status: examSyllabus?.status,
      examDate: examSyllabus?.examDate as unknown as string,
    },
  });

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <TextField
          title="Tittel"
          fieldProps={methods.register("title", {
            required: REQUIRED_FORM_FIELD,
          })}
        />
        <Controller
          name="examDate"
          control={methods.control}
          rules={{
            required: REQUIRED_FORM_FIELD,
          }}
          render={({ field }) => (
            <React.Suspense fallback={<Spinner />}>
              <DatePicker
                name={field.name}
                value={field.value as unknown as LocalDate}
                label="Eksamensdato"
                placeholder="Velg dato"
                minDate={new Date()}
                onChange={field.onChange}
              />
            </React.Suspense>
          )}
        />
        <FieldError name="examDate" />

        {!canPublish && (
          <>
            <Label htmlFor="studyProgramme" title="Studieprogram" />
            <Controller
              name="studyProgramme"
              control={methods.control}
              render={({ field }) => (
                <ReactSelect id="studyProgramme" isDisabled={true} {...field} />
              )}
            />
          </>
        )}

        <InputField
          id="studyPoints"
          type="number"
          title="Studiepoeng"
          register={methods.register("studyPoints", {
            valueAsNumber: true,
            validate: validateStudyPoints,
          })}
        />
        <FieldError name="studyPoints" />

        <MarkdownField<ExamSyllabusFormValues>
          name="description"
          title="Beskrivelse av pensum"
          required={true}
        />

        {canPublish && (
          <Select
            title="Status på pensum"
            options={contentStatusOptions()}
            fieldProps={methods.register("status")}
          />
        )}

        <MutationLoadingOrError loading={loading} error={error} />

        <CancelOrSubmit
          onCancel={onCancel}
          submitText={submitText || undefined}
          isLoading={loading}
        />
      </form>
    </FormProvider>
  );
};
